import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    DialogProps,
} from '@mui/material';
import QuestionViewer from './QuestionViewer';
import { goApi, codesToNames } from '../../services';
import { apiRoutes } from '../../services/apiRoutes';
import { LoadingSection } from '../../common/LoadingSection';

interface QuestionShowModalProps extends DialogProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    showActions?: boolean;
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
    record: any;
}

const QuestionShowModal: React.FC<QuestionShowModalProps> = ({
    open,
    record,
    onClose,
    title,
    showActions = true,
    cancelText = 'Close',
    ...rest
}) => {
    const [questionDetails, setQuestionDetails] = useState({}) as any;
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        const getQuestionDetails = async () => {
            try {
                const response = await goApi.get(apiRoutes.QUESTIONS_BANK_DETAIL(record?.id));

                const questionData = response?.data;
                if (!questionData) {
                    return;
                }

                const { langQuestions = [] } = questionData;

                if (langQuestions.length === 0) {
                    setQuestionDetails({
                        question: {
                            name: questionData.name || '',
                            content: questionData.content || '',
                            image: questionData.image || null,
                            solutionDescription: questionData.solutionDescription || '',
                            solutionImage: questionData.solutionImage || null,
                            type: questionData.type,
                        },
                        options: [],
                        questionLang: [],
                        optionLang: [],
                    });
                    setLoading(false);
                    return;
                }

                const langCodes = langQuestions.map((lq: any) => lq.language || 'en');
                const langNames = await codesToNames(langCodes);

                const mainQuestion = langQuestions[0] || {};
                const mainLanguageName = langNames[0] || 'English';

                const mappedLangQuestions = langQuestions.map((lq: any, idx: number) => ({
                    name: lq.name || '',
                    content: lq.content || '',
                    image: lq.image || null,
                    solutionDescription: lq.solutionDescription || '',
                    solutionImage: lq.solutionImage || null,
                    language: langNames[idx] || 'English',
                }));

                const baseOptions = (mainQuestion.options || []).map((opt: any, idx: number) => ({
                    id: opt.optionId || opt.id || idx,
                    content: opt.content,
                    image: opt.image,
                    is_correct: opt.isCorrect || opt.is_correct || false,
                    displayOrder: opt.displayOrder || idx,
                }));

                const optionTranslations: any[] = [];
                langQuestions.forEach((lq: any, langIndex: number) => {
                    (lq.options || []).forEach((opt: any, optIndex: number) => {
                        const mainOpt = baseOptions[optIndex];
                        if (mainOpt && langNames[langIndex] !== mainLanguageName) {
                            optionTranslations.push({
                                option_id: mainOpt.id,
                                content: opt.content,
                                image: opt.image,
                                language: langNames[langIndex],
                            });
                        }
                    });
                });

                const mapped = {
                    question: {
                        name: mainQuestion.name || questionData.name || '',
                        content: mainQuestion.content || questionData.content || '',
                        image: mainQuestion.image || questionData.image || null,
                        solutionDescription:
                            mainQuestion.solutionDescription ||
                            questionData.solutionDescription ||
                            '',
                        solutionImage:
                            mainQuestion.solutionImage || questionData.solutionImage || null,
                        language: mainLanguageName,
                        type: questionData.type,
                        positiveMarks: questionData.positiveMarks,
                        negativeMarks: questionData.negativeMarks,
                    },
                    options: baseOptions,
                    questionLang: mappedLangQuestions,
                    optionLang: optionTranslations,
                };

                setQuestionDetails(mapped);
            } catch (error) {
            } finally {
                setLoading(false);
            }
        };

        if (record?.id) {
            getQuestionDetails();
        }
    }, [record?.id]);

    return (
        <Dialog
            PaperProps={{
                sx: {
                    width: '800px',
                    height: 'auto',
                    maxWidth: 'none',
                },
            }}
            open={open}
            onClose={onClose}
            {...rest}
        >
            <div className="min-w-[500px] ">
                <DialogTitle>
                    {title} ({record?.name || record?.id})
                </DialogTitle>

                <DialogContent dividers>
                    {loading ? <LoadingSection /> : <QuestionViewer data={questionDetails} />}
                </DialogContent>

                {showActions && (
                    <DialogActions>
                        <Button onClick={onClose}>{cancelText}</Button>
                    </DialogActions>
                )}
            </div>
        </Dialog>
    );
};
export default QuestionShowModal;
